<template>
  <div class="padded" style="text-align: left;">

    <filter-bar
      v-if="!hideTop"
      :title="title"
      :icon="icon"
      :search-placeholder="searchPlaceholder"
      :show-search="showSearch"
      :show-refresh="showRefresh"
      :hide-org-badge="hideOrgBadge"
      :title-heading="titleHeading"
      :title-class="titleClass"
      :table-loading="loadingEvent"
      :table-loaded="loadedEvent"
      :set-filter-event="filterEvent"
      :reset-filter-event="filterReset"
      :refresh-event="refreshTable">
      <!-- Forward the `actions` slot into filter-bar so add-buttons share the
           same row as title + search. Keeps the `<hr />` below at full width. -->
      <template v-if="$slots.actions" #actions>
        <slot name="actions" />
      </template>
    </filter-bar>

    <!-- `below-filter` slot: sits between the filter-bar and the data rows.
         Mirrors the legacy vuetable-2 `bottom-comp` placement; used by
         SitesTable for the SiteMenu chip strip. -->
    <slot name="below-filter" />

    <!-- XOL-515: opt-in sort picker. Renders only when a consumer supplies
         `sortOptions`. All spacing lives as padding on `.xolas-table-sort` —
         no sibling margins — so it disappears cleanly (no seam, no collapsed
         gap) when a consumer nulls sortOptions (e.g. SitesTable in
         map-search mode). See docs/plans/2026-09-14-xol-515-sites-sort-controls.md
         UX spec for the full rationale (component choices, copy, a11y). -->
    <template v-if="hasSortPicker">
      <div class="xolas-table-sort">
        <label :for="tableRef + '-sort-field'" class="xolas-table-sort__label">Sort by</label>

        <el-select
          :id="tableRef + '-sort-field'"
          v-model="pickerField"
          size="small"
          popper-class="xolas-sort-popper"
          class="xolas-table-sort__field"
          @change="onSortFieldChange">
          <template v-if="hasSortGroups">
            <el-option-group v-for="group in groupedSortOptions" :key="group.name" :label="group.name">
              <el-option v-for="opt in group.options" :key="opt.value" :value="opt.value" :label="opt.label" />
            </el-option-group>
          </template>
          <template v-else>
            <el-option v-for="opt in normalisedSortOptions" :key="opt.value" :value="opt.value" :label="opt.label" />
          </template>
        </el-select>

        <!-- The word ships at every viewport width — icon-only was designed,
             reviewed, and rejected (see plan Decisions, 2026-09-14). No
             `title`, no `.xolas-table-sort__dir-text` span, no clip-hiding. -->
        <el-radio-group
          v-model="pickerDir"
          size="small"
          aria-label="Sort direction"
          class="xolas-table-sort__dir"
          @change="onSortDirChange">
          <el-radio-button label="asc">
            <i class="fa fa-arrow-up"></i> Ascending
          </el-radio-button>
          <el-radio-button label="desc">
            <i class="fa fa-arrow-down"></i> Descending
          </el-radio-button>
        </el-radio-group>

        <span class="xolas-table-sort__right">
          <!-- XOL-515 review fix: pickerFetchCount, not the bare isLoading
               flag — isLoading is true on first paint, page turns, search,
               and manual refresh too, none of which are a sort in progress.
               See the sortAnnouncement live region below, which was already
               correctly scoped to picker-initiated fetches; this matches it. -->
          <span v-if="pickerFetchCount > 0" class="xolas-table-sort__busy" aria-hidden="true">
            <i class="fa fa-spinner fa-spin"></i> Sorting…
          </span>
          <el-button
            v-else-if="isSortChanged"
            type="text"
            size="mini"
            class="xolas-table-sort__reset"
            @click="resetSort">Reset sort</el-button>
        </span>

        <!-- Visually hidden. aria-hidden on the busy text above prevents a
             double announcement — this is the single live region. -->
        <span class="xolas-table-sort__sr" role="status" aria-live="polite">{{ sortAnnouncement }}</span>
      </div>

      <el-alert
        v-if="sortError"
        type="error"
        show-icon
        :closable="true"
        role="alert"
        :title="'Couldn\'t sort by ' + sortError"
        description="The list below is still in the previous order. Choose a different field, or use the refresh button to try again."
        @close="sortError = null" />
    </template>

    <el-table
      v-loading="isLoading && !rows.length"
      :ref="tableRef"
      :data="rows"
      :row-key="trackBy"
      stripe
      :empty-text="''"
      @sort-change="onSortChange"
      @selection-change="onSel">

      <!-- Leading selection column: opt-in via :selectable="true". Renders a
           checkbox column (type="selection") with reserve-selection so checked
           rows survive page turns. row-key (trackBy) is required by Element UI
           for reserve-selection to work correctly. -->
      <el-table-column v-if="selectable" type="selection" width="48" :reserve-selection="true" />

      <!-- Leading chevron column: appears whenever a layer route or layer component is wired.
           Mirrors the vuetable-2 expand-row button so users muscle-memoried on
           the chevron keep working alongside the new Site-ID layer link. -->
      <el-table-column v-if="layerRoute || layerComponent" label="" width="70" align="center" class-name="xolas-table__chevron-col">
        <template slot-scope="scope">
          <el-button
            type="info"
            size="mini"
            plain
            round
            :aria-label="'Open detail for row ' + (scope.$index + 1)"
            @click="onRowClick(scope.row, $event)">
            <i class="fa fa-angle-right"></i>
          </el-button>
        </template>
      </el-table-column>

      <el-table-column
        v-for="field in visibleFields"
        :key="field.name"
        :prop="field.name.startsWith('__') ? undefined : field.name"
        :label="field.title"
        :sortable="field.sortField ? 'custom' : false"
        :class-name="field.dataClass"
        :label-class-name="field.titleClass"
        :width="field.width"
        :min-width="field.minWidth"
        :align="field.align">
        <template slot-scope="scope">
          <slot
            v-if="field.name.startsWith('__slot:')"
            :name="field.name.split(':')[1]"
            :row-data="scope.row"
            :row-index="scope.$index" />
          <span v-else-if="field.name === '__sequence'">{{ sequenceIndex(scope.$index) }}</span>
          <!-- eslint-disable-next-line vue/no-v-html -- callback output is developer-controlled HTML (vuetable-2 parity) -->
          <span v-else-if="field.callback" v-html="formatCell(field, scope.row)"></span>
          <a
            v-else-if="(layerRoute || layerComponent) && field === firstDataField"
            :href="resolveLayerHref(scope.row)"
            class="xolas-table__row-link"
            @click.prevent="onRowClick(scope.row, $event)"
            @mousedown="onRowMouseDown(scope.row, $event)">
            {{ formatCell(field, scope.row) }}
          </a>
          <span v-else>{{ formatCell(field, scope.row) }}</span>
        </template>
      </el-table-column>

      <el-table-column v-if="showEditButton || showDeleteButton" label="" width="120">
        <template slot-scope="scope">
          <el-button v-if="showEditButton" size="mini" @click="$emit('edit-click', scope.row)">Edit</el-button>
          <el-button v-if="showDeleteButton" type="danger" size="mini" @click="$emit('delete-click', scope.row)">Delete</el-button>
        </template>
      </el-table-column>

      <template #empty>
        <slot name="empty">
          <span class="text-muted">No records</span>
        </slot>
      </template>

    </el-table>

    <el-row v-if="showPagination && page.total > page.per_page">
      <el-col align="right" :span="24">
        <xolas-table-pagination
          ref="pagination"
          :page-data="page"
          @change-page="onChangePage" />
      </el-col>
    </el-row>

  </div>
</template>

<script>
import axios from '@/lib/axiosClient'
import ArrayMixins from '@/mixins/ArrayMixins'
import DateMixins from '@/mixins/DateMixins'
import FilterBar from '@/components/atoms/tables/DataTableFilterBar'
import XolasTablePagination from '@/components/atoms/tables/XolasTablePagination'
import { pushDrawer, resolveDrawerHref } from '@/lib/layerNav'

export default {
  name: 'xolas-table',

  mixins: [ArrayMixins, DateMixins],

  components: {
    FilterBar,
    XolasTablePagination
  },

  props: {
    url: {
      type: String,
      required: true
    },
    fields: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    tableRef: {
      type: String,
      default: 'xolastable'
    },
    trackBy: {
      type: String,
      default: 'id'
    },
    sortField: {
      type: [String, Array],
      default: 'id'
    },
    sortDirection: {
      type: String,
      default: 'asc'
    },
    // XOL-515: opt-in sort picker. Array of { value, label } (bare strings are
    // tolerated — label falls back to value). When non-empty AND !hideTop the
    // table renders its own sort bar and switches the wire format to the
    // legacy pipe form `sort=<field>|<dir>`, which is the ONLY form every
    // /sites handler understands (SiteController@getSharerSites reads
    // $request->input('sort') raw and BaseModel::doFilter discards a bare
    // field). Default null => byte-identical behaviour for every existing
    // consumer, which keeps the two-param sort/sort_dir form.
    sortOptions: {
      type: Array,
      default: null
    },
    perPage: {
      type: Number,
      default: 10
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...'
    },
    showSearch: {
      type: Boolean,
      default: true
    },
    showRefresh: {
      type: Boolean,
      default: true
    },
    // Legacy DataTable parity: suppress the <org-badge> prefix in the title
    // (e.g. FinanceSummary renders "Finance Statement", not "Xolas Finance
    // Statement"). Forwarded to DataTableFilterBar's existing hideOrgBadge.
    hideOrgBadge: {
      type: Boolean,
      default: false
    },
    // Legacy DataTable parity: the old wrapper defaulted its title heading to
    // h5 and forwarded it to DataTableFilterBar; without forwarding, the
    // filter bar's own h4 default made every migrated title one level larger
    // than its pre-migration render. Consumers that legacy-passed an explicit
    // title-heading (e.g. OrgTable's h2) carry it through this prop when they
    // migrate.
    titleHeading: {
      type: String,
      default: 'h5'
    },
    // Legacy DataTable parity: the old wrapper defaulted title-class to
    // 'text-info' (#909399); without it the heading falls back to the global
    // h5 color.
    titleClass: {
      type: String,
      default: 'text-info'
    },
    showPagination: {
      type: Boolean,
      default: true
    },
    hideTop: {
      type: Boolean,
      default: false
    },
    params: {
      type: Object,
      default: () => ({})
    },
    // layer wiring (legacy — deprecated in XOL-209 PR 2, use layerComponent instead)
    layerRoute: {
      type: String,
      default: null
    },
    layerParam: {
      type: String,
      default: 'id'
    },
    layerParamName: {
      type: String,
      default: 'id'
    },
    // layer wiring (XOL-209 PR 2 — d.<component>.<args> URL grammar)
    // layerComponent: registered drawer name (e.g. 'bill', 'site')
    layerComponent: {
      type: String,
      default: null
    },
    // layerArgs: rowData-key map per drawer arg (e.g. { id: 'id' })
    layerArgs: {
      type: Object,
      default: function () { return { id: 'id' } }
    },
    // category-B affordances — off by default in PR 1
    showEditButton: {
      type: Boolean,
      default: false
    },
    showDeleteButton: {
      type: Boolean,
      default: false
    },
    // opt-in multi-select: renders a leading checkbox column (type="selection").
    // When true, the el-table emits selection-change which XolasTable re-emits
    // as @selection-change. Use clearSelection() to programmatically deselect.
    selectable: {
      type: Boolean,
      default: false
    },
    // XOL-321 Phase B — select-all-with-exclusions.
    // autoSelectAll: when true, after each successful fetch() XolasTable
    // programmatically checks every row whose trackBy id is NOT in excludedIds.
    // The programmatic batch is wrapped in _suppressSelEmit so it does not
    // trigger @selection-change on the parent — only genuine user interactions do.
    // Default off: no behaviour change for existing consumers.
    autoSelectAll: {
      type: Boolean,
      default: false
    },
    // excludedIds: ids (values of the trackBy field) that should remain unchecked
    // during the autoSelectAll batch. Read only when autoSelectAll is true.
    excludedIds: {
      type: Array,
      default: function () { return [] }
    }
  },

  data () {
    return {
      rows: [],
      isLoading: true,
      page: {
        total: 0,
        current_page: 1,
        last_page: 1,
        per_page: this.perPage
      },
      filterText: '',
      // Picker state. sortField may already arrive pipe-formatted from a
      // consumer following the RequestTable pattern — strip the suffix.
      pickerField: String(this.sortField).split('|')[0],
      pickerDir: String(this.sortDirection).toLowerCase() === 'desc' ? 'desc' : 'asc',
      // XOL-515: sortError is set only for a picker-initiated fetch that 422s
      // (BaseModel::doFilter has no allowlist). lastGoodSort is the picker
      // state to revert to when that happens, so the control never lies about
      // the order actually on screen. sortAnnouncement feeds the visually
      // hidden live region — must stay in sync with the copy table in the UX
      // spec (Busy / SR success / SR failure).
      sortError: null,
      lastGoodSort: {
        field: String(this.sortField).split('|')[0],
        dir: String(this.sortDirection).toLowerCase() === 'desc' ? 'desc' : 'asc'
      },
      sortAnnouncement: '',
      // XOL-515 review fix: reactive counter of in-flight picker-initiated
      // fetches. The bar's busy text must show ONLY for a sort the user
      // actually triggered — not first paint, page turns, search, or manual
      // refresh (those all call fetch() directly, never applySort(), so this
      // counter never moves for them). A counter rather than a boolean so two
      // overlapping picker fetches don't have the earlier one's settlement
      // clear the busy state out from under the still-pending later one.
      pickerFetchCount: 0,
      // XOL-515 round 3: seed BARE, unconditionally — whatever the consumer
      // passed via sortField/sortDirection, untouched (this preserves
      // RequestTable's own pre-encoded ":sort-field=\"sortField + '|' + sortDir\""
      // pattern, which has nothing to do with sortOptions/hasSortPicker at
      // all). If the picker is active, created() below overwrites this via
      // encodePickerSort() — the SAME predicate (hasSortPicker) that gates
      // every other write of sortOrder. Do not re-introduce a second,
      // independently-derived predicate here: round 2 gated the field on
      // `this.sortOptions && this.sortOptions.length` (no hideTop term) while
      // every other writer gated on hasSortPicker (`!hideTop && ...`), so a
      // table mounted with `{ sortOptions, hideTop: true }` rendered no bar
      // yet still put a pipe on the wire on paint 1, then bare on paint 2 —
      // pipe on one request, two-param on the next, from a table with no
      // picker anywhere. See plan Decisions, 2026-09-15.
      sortOrder: [{
        field: this.sortField,
        direction: this.sortDirection
      }],
      moreParams: Object.assign({}, this.params),
      // Current window width, used to drop responsive (`hidden-*`) columns at
      // their Element-UI breakpoints. Defaults wide so non-browser envs (jsdom)
      // keep every column; the real value is read in mounted (browser only).
      winWidth: 1920
    }
  },

  computed: {
    visibleFields () {
      return this.fields.filter(f => f.visible !== false && !this.isHiddenAtWidth(f))
    },

    firstDataField () {
      // First data field eligible for the layer-link <a>: skip __-prefixed
      // specials (e.g. __sequence, __slot:*). Callback fields are allowed —
      // they may format the value but are still valid click targets (e.g.
      // SiteUtilTable's 'date' field). Only the __-prefix check is applied.
      return this.fields.find(f => !f.name.startsWith('__'))
    },

    loadedEvent () {
      return this.tableRef + '-loaded'
    },

    loadingEvent () {
      return this.tableRef + '-loading'
    },

    refreshTable () {
      return this.tableRef + '-refresh-table'
    },

    filterEvent () {
      return this.tableRef + '-filter-set'
    },

    filterReset () {
      return this.tableRef + '-filter-reset'
    },

    sortEvent () {
      return this.tableRef + '-sort-set'
    },

    // Picker is live only when options were supplied and the top strip renders.
    hasSortPicker () {
      return !this.hideTop && !!this.sortOptions && this.sortOptions.length > 0
    },

    // Normalise [{value,label,group?}] | ['field'] -> [{value,label,group}]
    normalisedSortOptions () {
      if (!this.sortOptions) return []
      return this.sortOptions.map(function (o) {
        return (o && typeof o === 'object')
          ? { value: o.value, label: o.label === undefined ? o.value : o.label, group: o.group }
          : { value: o, label: o, group: undefined }
      })
    },

    // True when any normalised option carries a `group` key. Options with no
    // group render a flat <el-option> list (the two legacy data-table
    // consumers don't have to invent group names).
    hasSortGroups () {
      return this.normalisedSortOptions.some(function (o) { return !!o.group })
    },

    // Buckets normalisedSortOptions into { name, options[] } groups, in
    // first-seen order. Only meaningful when hasSortGroups is true.
    groupedSortOptions () {
      var groups = []
      var byName = {}
      this.normalisedSortOptions.forEach(function (o) {
        var name = o.group || ''
        if (!byName[name]) {
          byName[name] = { name: name, options: [] }
          groups.push(byName[name])
        }
        byName[name].options.push(o)
      })
      return groups
    },

    // Label of the currently selected picker field — used in the error alert
    // title and the SR announcements.
    pickerFieldLabel () {
      var match = this.normalisedSortOptions.filter(o => o.value === this.pickerField)[0]
      return match ? match.label : this.pickerField
    },

    // "Reset sort" only renders once the live sort has drifted from the
    // declared default (sort-field / sort-direction props).
    isSortChanged () {
      var defaultField = String(this.sortField).split('|')[0]
      var defaultDir = String(this.sortDirection).toLowerCase() === 'desc' ? 'desc' : 'asc'
      return this.pickerField !== defaultField || this.pickerDir !== defaultDir
    }
  },

  methods: {
    // selection-change handler: re-emit so parent can bind @selection-change.
    // Guard: while _suppressSelEmit is true (set during applyAutoSelection's
    // programmatic toggleRowSelection batch) do NOT propagate to the parent —
    // this prevents the feedback loop where auto-checking rows triggers
    // BulkRateChange.onSelectionChange and wrongly resets excludedIds.
    onSel (rows) {
      if (this._suppressSelEmit) return
      this.$emit('selection-change', rows)
    },

    // Programmatically clear all checked rows (delegates to el-table).
    clearSelection () {
      var t = this.$refs[this.tableRef]
      if (t && t.clearSelection) t.clearSelection()
    },

    // Returns the trackBy ids of all rows currently loaded on this page.
    // Used by BulkRateChange to page-scope the exclude/re-include reconcile
    // (XOL-321 Phase B) — only current-page ids are touched per reconcile call;
    // rows on other pages are left alone.
    currentRowIds () {
      var self = this
      return this.rows.map(function (r) { return r[self.trackBy] })
    },

    // XOL-321 Phase B — programmatically check/uncheck current-page rows to
    // match the autoSelectAll + excludedIds state.  Called after a successful
    // fetch() (via $nextTick) and on excludedIds/autoSelectAll changes.
    // Feedback-loop containment: _suppressSelEmit is set true for the entire
    // batch so onSel() is a no-op during the toggle calls; cleared on the next
    // $nextTick after all toggles complete.
    applyAutoSelection () {
      if (!this.autoSelectAll) return
      var self = this
      var t = self.$refs[self.tableRef]
      if (!t || typeof t.toggleRowSelection !== 'function') return

      var excludedSet = {}
      ;(self.excludedIds || []).forEach(function (id) { excludedSet[id] = true })

      self._suppressSelEmit = true
      self.rows.forEach(function (row) {
        var id = row[self.trackBy]
        t.toggleRowSelection(row, !excludedSet[id])
      })
      self.$nextTick(function () {
        self._suppressSelEmit = false
      })
    },

    // Re-run el-table's column-width layout pass. Called after mount and after
    // each successful data load so the table fills its container correctly even
    // when rendered inside a drawer/component-swap that measures too early.
    relayout () {
      var t = this.$refs[this.tableRef]
      if (t && typeof t.doLayout === 'function') {
        t.doLayout()
      }
    },

    // Whether a field carries an Element-UI `hidden-*` responsive class that
    // hides it at the current window width. el-table uses fixed table-layout
    // with a <colgroup>: a column merely CSS-hidden (display:none) still
    // reserves its <col> width, leaving dead space (notably an empty gap at the
    // right edge when the hidden column is last, e.g. Total Deposit). vuetable-2
    // used auto layout where hidden cells collapse. To match that — and the dev
    // render — we drop the column from `visibleFields` entirely at the same
    // breakpoints the class encodes, so no width is reserved. Thresholds mirror
    // Element UI 2.x: xs<768, sm<992, md<1200, lg<1920, xl>=1920.
    isHiddenAtWidth (field) {
      var cls = (field.dataClass || '') + ' ' + (field.titleClass || '')
      if (cls.indexOf('hidden-') === -1) return false
      var w = this.winWidth
      if (/hidden-xs-only/.test(cls) && w < 768) return true
      if (/hidden-sm-and-down/.test(cls) && w < 992) return true
      if (/hidden-md-and-down/.test(cls) && w < 1200) return true
      if (/hidden-lg-and-down/.test(cls) && w < 1920) return true
      if (/hidden-xl-only/.test(cls) && w >= 1920) return true
      return false
    },

    fetch () {
      this.isLoading = true
      this.$events.emit(this.loadingEvent)

      // XOL-515: capture + clear the picker-initiated flag for THIS fetch
      // before any async boundary. A page turn / filter refetch that happens
      // to land while a picker fetch is also in flight must not mis-attribute
      // its own outcome to the sort bar.
      const isPickerFetch = this._pickerFetchInFlight
      this._pickerFetchInFlight = false

      // XOL-515 review fix: snapshot the picker state (and the page it is
      // replacing) AS ISSUED — not read live in .then/.catch. A second
      // picker fetch can be dispatched while this one is still in flight
      // (user changes field again before the first request settles); reading
      // live pickerField/pickerDir when THIS promise settles would let a
      // superseded response record lastGoodSort, announce an order, or
      // revert-on-failure using state that belongs to a newer request.
      const pickerSnapshot = isPickerFetch
        ? {
          field: this.pickerField,
          dir: this.pickerDir,
          label: this.pickerFieldLabel,
          prevPage: this._pickerFetchPrevPage
        }
        : null

      // XOL-515 round 3: guards whether the picker bookkeeping below has
      // already run for THIS fetch, so one fetch can never settle its picker
      // state twice (double-decrementing pickerFetchCount, or raising a
      // "couldn't sort" error for a request that returned 200).
      //
      // No reachable synchronous-throw point exists after that bookkeeping
      // TODAY. The only statements following it are $nextTick(this.relayout)
      // and the guarded $nextTick(this.applyAutoSelection) — $nextTick
      // enqueues, it never invokes synchronously, and Vue 2.7 additionally
      // wraps nextTick callbacks in invokeWithErrorHandling, so a throw from
      // either is both deferred AND caught before it could reject this
      // promise. This flag is therefore a FORWARD guard: it starts earning
      // its keep the moment someone changes `$nextTick(this.relayout)` to a
      // direct `this.relayout()`. Shipped deliberately untested — see the
      // 2026-09-15 entry in docs/plans/2026-09-14-xol-515-sites-sort-controls.md.
      //
      // The placement is the load-bearing part: `settled = true` is the FIRST
      // statement inside the picker block, so a throw BEFORE it still lets
      // .catch settle the request exactly once. That is not hypothetical —
      // this.$events.emit(this.loadedEvent) sits above it, and $events is
      // mitt, which (unlike Vue's $emit) does not wrap listeners, so a
      // throwing consumer listener there DOES propagate synchronously.
      let settled = false

      const sortField = this.sortOrder[0] ? this.sortOrder[0].field : this.sortField
      const sortDir = this.sortOrder[0] ? this.sortOrder[0].direction : this.sortDirection

      const params = Object.assign({}, this.moreParams, {
        page: this.page.current_page,
        per_page: this.page.per_page,
        sort: sortField,
        sort_dir: sortDir
      })

      if (this.filterText) {
        params.filter = this.filterText
      }

      // Match legacy DataTable: resolve the API base + org scoping via the
      // global Vue mixin's getEndpoint(). Without this the request hits the
      // SPA origin (e.g. localhost:8080/sites) and returns index.html, which
      // parses to an empty data set with no error surfaced.
      const apiUrl = typeof this.getEndpoint === 'function'
        ? this.getEndpoint(this.url)
        : this.url

      axios.get(apiUrl, { params }).then(res => {
        const data = res.data
        // Normalise the paginator's `data` to an array. Some endpoints (e.g.
        // /report/property/due) return `data` as a JSON object instead of an
        // array on certain pages — a Laravel quirk when the paginated collection
        // has non-sequential keys. el-table's :data requires an array (it would
        // render zero rows for an object), whereas the legacy vuetable tolerated
        // objects via v-for. Coerce object → values so pagination works either way.
        const pageRows = data.data
        this.rows = Array.isArray(pageRows)
          ? pageRows
          : (pageRows && typeof pageRows === 'object' ? Object.values(pageRows) : [])
        this.page = {
          total: data.total || 0,
          current_page: data.current_page || 1,
          last_page: data.last_page || 1,
          per_page: data.per_page || this.perPage
        }
        this.$emit('total-changed', this.page.total)
        this.isLoading = false
        this.$events.emit(this.loadedEvent)
        this.$emit('loaded', this.rows)
        // XOL-515: any successful fetch clears a stale sort error, whether or
        // not THIS fetch was picker-initiated (e.g. the user hit refresh after
        // a failed sort). Only a picker-initiated success updates the "last
        // good sort" and announces the new order — a page turn must not
        // narrate a sort that didn't happen.
        this.sortError = null
        if (isPickerFetch) {
          settled = true
          this.pickerFetchCount = Math.max(0, this.pickerFetchCount - 1)
          // Use the snapshot taken when THIS request was issued, not live
          // picker state — and only record/announce it if nothing newer has
          // superseded it. Without the match check, a stale 200 (request A)
          // that resolves after the user already moved on to a newer
          // selection (request B, still pending) would overwrite lastGoodSort
          // and announce an order that both contradicts the picker's current
          // selection and isn't necessarily what's on screen once B settles.
          if (this.pickerSnapshotIsCurrent(pickerSnapshot)) {
            this.lastGoodSort = { field: pickerSnapshot.field, dir: pickerSnapshot.dir }
            this.sortAnnouncement = 'Sorted by ' + pickerSnapshot.label + ', ' +
              (pickerSnapshot.dir === 'desc' ? 'descending' : 'ascending') + '.'
          }
        }
        // Re-run layout after rows are painted so column widths fill the
        // container correctly (fixes squished columns in drawer context).
        this.$nextTick(this.relayout)
        // XOL-321 Phase B: after rows are in the DOM, auto-check rows per the
        // excludedIds set. $nextTick ensures el-table has rendered the new rows
        // before toggleRowSelection is called on them.
        if (this.autoSelectAll) {
          this.$nextTick(this.applyAutoSelection)
        }
      }).catch(err => {
        this.isLoading = false
        this.$events.emit(this.loadedEvent)
        // XOL-515: only a picker-initiated fetch may raise the inline sort
        // alert (risk 2 — an unsupported field 422s). A page-change or
        // dropped-connection error must not produce a sentence about sorting.
        // The picker reverts to the last sort that actually succeeded WITHOUT
        // refetching — rows already on screen are in that order, so the
        // control tells the truth again. Uses the snapshot taken when THIS
        // request was issued (field/dir/label/prevPage), not live state —
        // and only acts if nothing newer has superseded this request, so a
        // late failure from an already-abandoned selection cannot stomp a
        // newer, still-in-flight one.
        if (isPickerFetch && !settled) {
          settled = true
          this.pickerFetchCount = Math.max(0, this.pickerFetchCount - 1)
          if (this.pickerSnapshotIsCurrent(pickerSnapshot)) {
            this.pickerField = this.lastGoodSort.field
            this.pickerDir = this.lastGoodSort.dir
            this.sortOrder = [{
              field: this.encodePickerSort(this.lastGoodSort.field, this.lastGoodSort.dir),
              direction: this.lastGoodSort.dir
            }]
            // Page number is not part of "the order on screen", but it is
            // part of "what the control claims" — a re-sort resets to page 1
            // before the request goes out; on failure the paginator must not
            // be left reading page 1 while the rows on screen are still the
            // previous page's.
            this.page.current_page = pickerSnapshot.prevPage
            this.sortError = pickerSnapshot.label
            this.sortAnnouncement = 'Couldn\'t sort by ' + pickerSnapshot.label + '. The list is unchanged.'
          }
        }
        this.$emit('error', err)
      })
    },

    refresh () {
      this.fetch()
    },

    // XOL-515 review fix: pipe encoding must be gated on hasSortPicker, not
    // applied unconditionally. `applySort()` is the single writer reached by
    // the bar's own handlers (gated by hasSortPicker in the template) AND by
    // onSortSet, whose $events.on listener is registered on EVERY XolasTable
    // — including the ~30 with no sortOptions. Before this fix, containment
    // of the pipe format to opted-in tables was accidental (it relied on no
    // code ever emitting `<tableRef>-sort-set` at a non-picker table), not
    // enforced by this component. Centralising the encoding here means the
    // bar's handlers, the header-click branch, and the event listener all
    // get the guard for free.
    encodePickerSort (field, dir) {
      return this.hasSortPicker ? (field + '|' + dir) : field
    },

    // XOL-515 round 3: single guard expression for "does this settled
    // request's picker snapshot still match the live selection" — shared by
    // fetch()'s .then and .catch so a future edit to one cannot silently
    // diverge from the other. Round 2 shipped the same comparison
    // copy-pasted at both call sites; mutation-testing the .catch copy alone
    // (replacing it with `if (true)`) left the entire suite green, because
    // no test exercised a superseded picker fetch failing (only succeeding).
    // See test 'overlapping picker fetches (mirror)' below and plan
    // Decisions, 2026-09-15.
    pickerSnapshotIsCurrent (pickerSnapshot) {
      return !!pickerSnapshot && pickerSnapshot.field === this.pickerField && pickerSnapshot.dir === this.pickerDir
    },

    // XOL-515 — Single writer for picker-driven sort. Encodes the pipe form
    // (gated on hasSortPicker — see encodePickerSort), resets to page 1 (a
    // re-sort invalidates the current page) while remembering the page being
    // left so a failed sort can restore it, marks the in-flight request as
    // picker-initiated (so fetch() knows whether a 422 belongs to the sort
    // bar), and refetches.
    applySort () {
      this.sortOrder = [{
        field: this.encodePickerSort(this.pickerField, this.pickerDir),
        direction: this.pickerDir
      }]
      this._pickerFetchInFlight = true
      this._pickerFetchPrevPage = this.page.current_page
      this.pickerFetchCount++
      this.sortAnnouncement = 'Sorting…'
      this.page.current_page = 1
      this.fetch()
    },

    onSortFieldChange (value) {
      this.pickerField = value
      this.applySort()
    },

    onSortDirChange (value) {
      this.pickerDir = value
      this.applySort()
    },

    // Returns the picker to the declared default (sort-field / sort-direction
    // props) and re-applies. Nothing is destroyed, so no confirm.
    resetSort () {
      this.pickerField = String(this.sortField).split('|')[0]
      this.pickerDir = String(this.sortDirection).toLowerCase() === 'desc' ? 'desc' : 'asc'
      this.applySort()
    },

    // `<tableRef>-sort-set` — the channel DataTableFilterBar has always emitted
    // ({sortField, sortDirection}). XOL-209 PR 1 promised this listener and
    // never landed it; that omission is half of why /sites lost its sort.
    // Mirrors DataTable.vue:459.
    onSortSet (sort) {
      if (!sort || !sort.sortField) return
      this.pickerField = String(sort.sortField).split('|')[0]
      if (sort.sortDirection) {
        this.pickerDir = String(sort.sortDirection).toLowerCase() === 'desc' ? 'desc' : 'asc'
      }
      this.applySort()
    },

    // A layerArgs value is the rowData key to read, or (XOL-596 completed steps) a function
    // of the row, for an arg that is not a column (the equipment drawer's step context).
    layerArgValue (rowKey, rowData) {
      return typeof rowKey === 'function' ? rowKey(rowData) : rowData[rowKey]
    },

    resolveLayerHref (rowData) {
      if (this.layerComponent) {
        const args = {}
        for (const [argName, rowKey] of Object.entries(this.layerArgs)) {
          args[argName] = this.layerArgValue(rowKey, rowData)
        }
        return resolveDrawerHref(this.$router, { open: { [this.layerComponent]: args } })
      }
      // deprecated: layerRoute path
      if (!this.layerRoute) return '#'
      const resolved = this.$router.resolve({
        name: this.layerRoute,
        params: { [this.layerParamName]: rowData[this.layerParam] }
      })
      return resolved.href
    },

    onRowMouseDown (rowData, event) {
      if (!this.layerRoute && !this.layerComponent) return
      const isNewTab = event.metaKey || event.ctrlKey || event.button === 1
      if (isNewTab) {
        event.preventDefault()
        window.open(this.resolveLayerHref(rowData), '_blank')
      }
    },

    onRowClick (rowData, event) {
      this.$emit('row-click', rowData)
      if (this.layerComponent) {
        const args = {}
        for (const [argName, rowKey] of Object.entries(this.layerArgs)) {
          args[argName] = this.layerArgValue(rowKey, rowData)
        }
        pushDrawer(this.$router, { open: { [this.layerComponent]: args } })
      } else if (this.layerRoute) {
        // deprecated: layerRoute path — kept for one PR cycle
        this.$router.push({
          name: this.layerRoute,
          params: { [this.layerParamName]: rowData[this.layerParam] }
        })
      }
    },

    onSortChange ({ column, prop, order }) {
      if (!prop) return
      const dir = order === 'ascending' ? 'asc' : 'desc'
      // A picker-enabled table must not let an el-table header click clobber
      // the pipe form — route it through the same writer as the picker.
      if (this.hasSortPicker) {
        this.pickerField = prop
        this.pickerDir = dir
        this.applySort()
        return
      }
      this.sortOrder = [{ field: prop, direction: dir }]
      this.fetch()
    },

    onChangePage (page) {
      this.page.current_page = page
      this.fetch()
    },

    onFilterSet (filterText) {
      this.filterText = filterText
      this.page.current_page = 1
      this.fetch()
    },

    onFilterReset () {
      this.filterText = ''
      this.moreParams = Object.assign({}, this.params)
      this.page.current_page = 1
      this.fetch()
    },

    sequenceIndex (rowIndex) {
      // 1-based, page-aware (matches vuetable-2 __sequence semantics)
      const offset = (this.page.current_page - 1) * this.page.per_page
      return offset + rowIndex + 1
    },

    // Returns the count of rows currently loaded in this page.
    // Used by BulkRateChange to detect page-fully-selected for the
    // "select all N matching" banner (Phase B, XOL-321).
    currentRowCount () {
      return this.rows.length
    },

    formatCell (field, row) {
      // Resolve nested property path (e.g. 'tenancy.utilAccount')
      const value = field.name.split('.').reduce((obj, key) => {
        return obj && obj[key] !== undefined ? obj[key] : null
      }, row)

      if (!field.callback) return value

      if (typeof field.callback === 'function') {
        return field.callback(value)
      }

      if (typeof field.callback === 'string') {
        const parts = field.callback.split('|')
        const methodName = parts[0]
        const arg = parts[1]
        if (typeof this[methodName] === 'function') {
          return arg ? this[methodName](value, arg) : this[methodName](value)
        }
      }

      return value
    }
  },

  watch: {
    // Keep internal sortOrder in sync with reactive sort-field/sort-direction
    // props. data() initializes sortOrder ONCE; without this watcher the
    // fetch() fallback `this.sortOrder[0] ? ... : this.sortField` always picks
    // the stale initial sortOrder. RequestTable changes :sort-field on the fly
    // via its sort dropdown — this watcher makes that change actually take.
    // refresh() is intentionally NOT called here; consumers own when to refetch.
    // XOL-515: a consumer using `sortOptions` (the picker) should pass a
    // STATIC sort-field/sort-direction — these watchers exist for RequestTable's
    // own reactive prop, and firing them on a picker-enabled table would stomp
    // the user's in-progress picker selection with the unchanged default.
    sortField (val) {
      this.sortOrder = [{ field: val, direction: this.sortDirection }]
    },
    sortDirection (val) {
      this.sortOrder = [{ field: this.sortField, direction: val }]
    },
    // XOL-321 Phase B: re-apply auto-selection when excludedIds or autoSelectAll
    // changes (e.g. user re-includes a previously excluded row from the parent).
    // Suppression-guarded via applyAutoSelection itself.
    excludedIds () {
      if (this.autoSelectAll) this.applyAutoSelection()
    },
    autoSelectAll (val) {
      if (val) this.applyAutoSelection()
    }
  },

  created () {
    // XOL-321 Phase B: non-reactive instance field for the feedback-loop
    // suppression flag. Matches the _resizeObserver/_rafId pattern used for
    // the resize observer. Not in data() because reactivity is neither needed
    // nor desirable — the flag must be set/cleared synchronously around the
    // toggleRowSelection batch, and Vue's setter would add unnecessary overhead.
    this._suppressSelEmit = false
    // XOL-515: non-reactive flag marking whether the currently in-flight
    // fetch() call was triggered by the sort picker (applySort/onSortChange)
    // vs. pagination/filter/refresh. Read and cleared synchronously at the
    // top of fetch(), before the axios call resolves.
    this._pickerFetchInFlight = false
    // XOL-515 review fix: page.current_page as it stood immediately before
    // applySort() reset it to 1, for THIS in-flight picker fetch. Captured
    // synchronously in applySort() (no async boundary before fetch() reads
    // it), so two overlapping picker fetches each get their own correct
    // "page to restore on failure" rather than reading whatever the other
    // one last wrote.
    this._pickerFetchPrevPage = 1
    // XOL-515 round 3: single decision point for the wire format. data()
    // seeds sortOrder bare; if the picker is active, overwrite it here via
    // encodePickerSort() — the same gate (hasSortPicker) every other writer
    // uses. Computeds are already live in created(), and mounted()'s fetch()
    // has not run yet, so the very first request still carries the right
    // value. This is now the ONLY place sortOrder's initial value is
    // pipe-encoded — it must never grow a second, separately-derived
    // predicate (see the comment on the data() seed above).
    if (this.hasSortPicker) {
      this.sortOrder = [{
        field: this.encodePickerSort(this.pickerField, this.pickerDir),
        direction: this.pickerDir
      }]
    }
  },

  mounted () {
    this.$events.on(this.filterEvent, this.onFilterSet)
    this.$events.on(this.filterReset, this.onFilterReset)
    this.$events.on(this.refreshTable, this.refresh)
    this.$events.on(this.sortEvent, this.onSortSet)
    this.fetch()
    // Re-run layout once the container has settled (fixes squished columns in
    // drawer/component-swap contexts where el-table measures too early).
    this.$nextTick(this.relayout)
    // Watch for container resize and re-layout. Guard for jsdom and older envs
    // that don't provide ResizeObserver.
    if (typeof ResizeObserver !== 'undefined') {
      var self = this
      // Read the real window width now (browser only) so responsive `hidden-*`
      // columns resolve correctly on first paint; keep it in sync on resize.
      if (typeof window !== 'undefined' && window.innerWidth) {
        this.winWidth = window.innerWidth
      }
      this._rafId = null
      this._resizeObserver = new ResizeObserver(function () {
        // Debounce via requestAnimationFrame to coalesce rapid resize events.
        if (typeof requestAnimationFrame !== 'undefined') {
          if (self._rafId !== null) cancelAnimationFrame(self._rafId)
          self._rafId = requestAnimationFrame(function () {
            self._rafId = null
            if (typeof window !== 'undefined' && window.innerWidth) {
              self.winWidth = window.innerWidth
            }
            self.relayout()
          })
        } else {
          if (typeof window !== 'undefined' && window.innerWidth) {
            self.winWidth = window.innerWidth
          }
          self.relayout()
        }
      })
      this._resizeObserver.observe(this.$el)
    }
  },

  beforeDestroy () {
    this.$events.off(this.filterEvent, this.onFilterSet)
    this.$events.off(this.filterReset, this.onFilterReset)
    this.$events.off(this.refreshTable, this.refresh)
    this.$events.off(this.sortEvent, this.onSortSet)
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
    if (this._rafId !== null && this._rafId !== undefined && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this._rafId)
      this._rafId = null
    }
  }
}
</script>

<style scoped>
.xolas-table__row-link {
  color: inherit;
  text-decoration: none;
  cursor: pointer;
}

.xolas-table__row-link:hover {
  text-decoration: underline;
  color: #409eff;
}

/* Chevron column uses Element UI's built-in
   `el-button--info el-button--mini is-plain is-round` classes (matches the
   legacy vuetable-2 expand-row button). No custom dimensions needed —
   Element UI handles spacing, color, and border via type/size/plain/round. */

/* el-table wraps header labels by default: a long single-word label plus the
   sort caret (e.g. "Spectrum") spills onto a second line, doubling header
   height. The legacy vuetable kept headers on one line. Force nowrap so column
   headers stay single-line and the header height matches the old table
   (XOL-231). Columns must be wide enough to avoid clipping. */
::v-deep .el-table th .cell {
  white-space: nowrap;
}

/* Legacy vuetable-2 parity: the old table showed a sort icon ONLY on the
   actively sorted column; el-table renders its double-caret on every sortable
   header, which (a) diverges visually from the pre-migration render and
   (b) widens/wraps narrow headers like "KM Ref". Hide the caret unless the
   column is actively sorted (Element UI sets .ascending/.descending on the th
   after a header click). Header clicks still sort — only the idle affordance
   is hidden, matching the legacy render (XOL-234 PR 8a dev-match). */
::v-deep .el-table th:not(.ascending):not(.descending) .caret-wrapper {
  display: none;
}

/* XOL-515 sort bar. All spacing is padding on .xolas-table-sort — no sibling
   margins — so the bar vanishes cleanly (no seam, no collapsed gap) when a
   consumer nulls sortOptions (e.g. SitesTable in map-search mode). Tint
   #F2F6FC matches .bg-bordered / SiteMenu's el-menu / .request-sort-bar so
   the chip strip and this bar read as one control band. */
.xolas-table-sort {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  background-color: #F2F6FC;
  padding: 8px 16px;
  border-bottom: 1px solid #EBEEF5;
}

.xolas-table-sort__label {
  font-size: 13px;
  color: #606266;
  flex: 0 0 auto;
  cursor: pointer;
}

.xolas-table-sort__field {
  flex: 0 0 220px;
}

.xolas-table-sort__dir {
  flex: 0 0 auto;
}

.xolas-table-sort__right {
  margin-left: auto;
  min-width: 132px;
  text-align: right;
  flex: 0 0 auto;
}

.xolas-table-sort__busy {
  font-size: 12px;
  color: #909399;
}

.xolas-table-sort__busy .fa {
  margin-right: 5px;
}

.xolas-table-sort__reset {
  font-size: 12px;
}

/* Visually hidden (clip technique, not display:none — display:none is
   dropped from the accessibility tree and would silence the live region). */
.xolas-table-sort__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* lg (769–1024px): select flexes so the direction control never wraps alone. */
@media (max-width: 1024px) {
  .xolas-table-sort__field {
    flex: 1 1 180px;
    max-width: 220px;
  }
}

/* md (481–768px): wrap to two rows — row 1 label+select, row 2 direction+right.
   sm/xs (<=480px): same two-row shape; only button padding tightens below —
   the arrow AND the word stay at every width (see plan Decisions, 2026-09-14:
   icon-only was designed, reviewed, and rejected). */
@media (max-width: 768px) {
  .xolas-table-sort__field {
    flex: 1 1 auto;
    min-width: 0;
  }
  .xolas-table-sort__dir {
    margin-left: 0;
  }
}

@media (max-width: 480px) {
  ::v-deep .xolas-table-sort__dir .el-radio-button__inner {
    padding-left: 10px;
    padding-right: 10px;
  }
}
</style>

<style>
/* Non-scoped: the select popper is portaled to document.body and does not
   carry XolasTable's data-v attribute. Targeted via popper-class so no other
   el-select dropdown in the app is affected. Sized for 10 options + 2 group
   titles (10*34 + 2*30 + 24px group separator + 12px list padding). Re-check
   if a consumer ships a longer list. Mirrors the documented precedent at
   SelectSite.vue:545-562. */
.xolas-sort-popper .el-select-dropdown__wrap {
  max-height: 440px;
}
</style>
